import type { IconButtonProps } from '@mui/material/IconButton';
import type { RootState } from 'src/state';
import type { IJob } from 'src/state/jobs';

import { m } from 'framer-motion';
import { gql } from '@apollo/client';
import { useBoolean } from 'minimal-shared/hooks';
import { useQuery, useMutation } from '@apollo/client/react';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { setJobs } from 'src/state/jobs';
import { useDispatch, useSelector } from 'src/state';
import { markAllAsRead, selectAllMessages, selectUnreadCount } from 'src/state/messages';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { NotificationItem } from './notification-item';

const MARK_ALL_MESSAGES_AS_READ = gql`
  mutation MarkAllMessagesAsRead {
    markAllMessagesAsRead
  }
`;

const GET_USER_JOBS = gql`
  query GetUserJobs {
    getUserJobs {
      jobId
      user_id
      type
      status
      progress
      metadata {
        contentId
        contentType
        workspaceId
        folderId
        key
        title
      }
      created_at
      updated_at
      completed_at
      error
      result
    }
  }
`;

// ----------------------------------------------------------------------

export type NotificationsDrawerProps = IconButtonProps;

export function NotificationsDrawer({ sx, ...other }: NotificationsDrawerProps) {
  const dispatch = useDispatch();
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();
  const [markAllMessagesAsReadMutation] = useMutation(MARK_ALL_MESSAGES_AS_READ);

  const [currentTab, setCurrentTab] = useState('notifications');

  // Fetch user jobs from GraphQL
  const { data: jobsData } = useQuery<{ getUserJobs: IJob[] }>(GET_USER_JOBS, {
    fetchPolicy: 'network-only',
  });

  // Update Redux state when jobs are fetched
  useEffect(() => {
    if (jobsData?.getUserJobs) {
      dispatch(setJobs(jobsData.getUserJobs));
    }
  }, [jobsData, dispatch]);

  // Get messages from Redux state
  const allMessages = useSelector(selectAllMessages);
  const unreadCount = useSelector(selectUnreadCount);

  // Get jobs from Redux state
  const jobs = useSelector((state: RootState) => state.jobs.jobs);
  const activeJobs = useMemo(
    () => jobs.filter((job: IJob) => job.status === 'active' || job.status === 'waiting'),
    [jobs]
  );

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    // Update local state immediately
    dispatch(markAllAsRead());

    // Update on server
    try {
      await markAllMessagesAsReadMutation();
    } catch (error) {
      console.error('Failed to mark all messages as read:', error);
    }
  }, [dispatch, markAllMessagesAsReadMutation]);

  // Dynamic tab counts
  const tabs = useMemo(
    () => [
      {
        value: 'notifications',
        label: 'Notifications',
        count: unreadCount > 0 ? unreadCount : allMessages.length,
      },
      { value: 'jobs', label: 'Jobs', count: activeJobs.length },
    ],
    [unreadCount, allMessages.length, activeJobs.length]
  );

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        minHeight: 68,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!unreadCount && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={handleMarkAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <IconButton onClick={onClose} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>

      <IconButton>
        <Iconify icon="solar:settings-bold-duotone" />
      </IconButton>
    </Box>
  );

  const renderTabs = () => (
    <Tabs variant="fullWidth" value={currentTab} onChange={handleChangeTab} indicatorColor="custom">
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          icon={
            <Label
              variant={(tab.value === currentTab && 'filled') || 'soft'}
              color={(tab.value === 'jobs' && 'info') || 'default'}
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </Tabs>
  );

  const renderList = () => (
    <Scrollbar>
      <Box component="ul">
        {currentTab === 'notifications' ? (
          // Display notifications
          allMessages.length > 0 ? (
            allMessages.map((message) => (
              <Box component="li" key={message.id} sx={{ display: 'flex' }}>
                <NotificationItem
                  notification={{
                    id: message.id,
                    type: message.type,
                    title: message.title,
                    category: message.category,
                    isUnRead: !message.isRead,
                    avatarUrl: message.avatarUrl,
                    createdAt: message.createdAt,
                    toastSettings: message.toastSettings,
                  }}
                />
              </Box>
            ))
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
              }}
            >
              <Iconify
                icon="solar:bell-bing-bold-duotone"
                width={64}
                sx={{ mb: 2, opacity: 0.5 }}
              />
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            </Box>
          )
        ) : // Display jobs
        activeJobs.length > 0 ? (
          activeJobs.map((job: IJob) => (
            <Box
              component="li"
              key={job.jobId}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2">
                  {job.type === 'content_import' ? 'Import' : 'Export'} -{' '}
                  {job.metadata.contentType || 'H5P'}
                </Typography>
                <Label
                  variant="soft"
                  color={
                    job.status === 'completed'
                      ? 'success'
                      : job.status === 'failed'
                        ? 'error'
                        : job.status === 'active'
                          ? 'info'
                          : 'default'
                  }
                >
                  {job.status}
                </Label>
              </Box>
              {job.status === 'active' && (
                <Box sx={{ width: '100%', mb: 1 }}>
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 1,
                      bgcolor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: '100%',
                        width: `${job.progress}%`,
                        bgcolor: 'primary.main',
                        transition: 'width 0.3s ease-in-out',
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {job.progress}%
                  </Typography>
                </Box>
              )}
              {job.error && (
                <Typography variant="caption" color="error.main">
                  {job.error}
                </Typography>
              )}
            </Box>
          ))
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <Iconify icon="solar:bill-list-bold-duotone" width={64} sx={{ mb: 2, opacity: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              No active jobs
            </Typography>
          </Box>
        )}
      </Box>
    </Scrollbar>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label="Notifications button"
        onClick={onOpen}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify width={24} icon="solar:bell-bing-bold-duotone" />
        </Badge>
      </IconButton>

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
          paper: { sx: { width: 1, maxWidth: 420 } },
        }}
      >
        {renderHead()}
        {renderTabs()}
        {renderList()}
      </Drawer>
    </>
  );
}
